import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Rocket, Shield, Zap } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { EarlyAccessProps } from '@/types';

/**
 * Early Access Section Component
 * Displays early access information and call-to-action
 */
export function EarlyAccess({ data, className }: EarlyAccessProps) {
  return (
    <section id="early-access" className={cn("py-20 bg-gradient-subtle", className)}>
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center space-y-6 mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-gradient">
            {data.title}
          </h2>
          
          <h3 className="text-xl lg:text-2xl font-semibold text-foreground">
            {data.subtitle}
          </h3>
          
          <p
            className="text-lg text-muted-foreground max-w-4xl mx-auto leading-relaxed"
            dangerouslySetInnerHTML={{ __html: data.description }}
          />
          
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 max-w-3xl mx-auto">
            <p className="text-sm text-primary font-medium">
              {data.note}
            </p>
          </div>
        </div>

        {/* Feature Highlights */}
        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-12">
          <Card className="gradient-card border-border/10 shadow-card">
            <CardContent className="p-6 text-center">
              <Shield className="w-8 h-8 text-primary mx-auto mb-4" />
              <h4 className="font-semibold mb-2">Secure Execution</h4>
              <p className="text-sm text-muted-foreground">
                Full audit controls with enterprise-grade security
              </p>
            </CardContent>
          </Card>
          
          <Card className="gradient-card border-border/10 shadow-card">
            <CardContent className="p-6 text-center">
              <Zap className="w-8 h-8 text-primary mx-auto mb-4" />
              <h4 className="font-semibold mb-2">Real-Time Action</h4>
              <p className="text-sm text-muted-foreground">
                Instant execution without scripting or emulation
              </p>
            </CardContent>
          </Card>
          
          <Card className="gradient-card border-border/10 shadow-card">
            <CardContent className="p-6 text-center">
              <Rocket className="w-8 h-8 text-primary mx-auto mb-4" />
              <h4 className="font-semibold mb-2">Preview Ready</h4>
              <p className="text-sm text-muted-foreground">
                Already deployed in enterprise infrastructure
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Call to Action */}
        <div className="text-center">
          <Button 
            size="lg"
            className="bg-primary hover:bg-primary/90 shadow-glow px-8 py-3 text-lg"
          >
            Apply for Early Access
          </Button>
          <p className="text-sm text-muted-foreground mt-4">
            Start adaptive automation today. Evolve operationally tomorrow.
          </p>
        </div>
      </div>
    </section>
  );
}
